import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Eye, X, Camera } from "lucide-react";
import { Reveal } from "@/components/Motion";
import { useAuth } from "@/context/AuthContext";
import api, { fmtErr } from "@/lib/api";
import PortfolioManager from "@/components/PortfolioManager";
import WorkHistoryEditor from "@/components/WorkHistoryEditor";
import ReelManager from "@/components/ReelManager";
import DocumentsManager from "@/components/DocumentsManager";
import ChangePassword from "@/components/ChangePassword";
import { PortfolioView } from "@/pages/ModelDetail";
import BookStrength from "@/components/BookStrength";
import { usePageMeta } from "@/lib/seo";
import { cmToInches, splitHeight, heightToCm, inchesToCm } from "@/lib/units";

const FIELDS = [
  ["name", "Full name", "text"],
  ["city", "City", "text"],
  ["gender", "Gender (Female / Male / Non-binary)", "text"],
  ["shoe", "Shoe size", "text"],
  ["hair", "Hair", "text"],
  ["eyes", "Eyes", "text"],
  ["instagram", "Instagram (@handle)", "text"],
];

const BODY_MEASUREMENTS = [
  ["bust", "Bust / Chest"],
  ["waist", "Waist"],
  ["hips", "Hips"],
];

const STATUS_STYLE = {
  pending: "border-white/25 text-white/60",
  reviewed: "border-white/25 text-white/60",
  shortlisted: "border-accent text-accent",
  booked: "border-accent bg-accent text-white",
  rejected: "border-white/10 text-white/30 line-through",
  held: "border-white/10 text-white/30",
};

export default function ModelDashboard() {
  usePageMeta({ title: "Your Book | Solis Casting", noindex: true });
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({});
  const [categories, setCategories] = useState("");
  const [bio, setBio] = useState("");
  const [apps, setApps] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [replyText, setReplyText] = useState({});
  const [sendingReply, setSendingReply] = useState(null);

  useEffect(() => {
    if (user?.profile) {
      const { bio: b, categories: c, ...rest } = user.profile;
      const h = splitHeight(rest.height_cm);
      setForm({
        name: user.name, ...rest,
        height_ft: h.ft, height_in: h.inch,
        bust_in: rest.bust ? Math.round(cmToInches(rest.bust)) : "",
        waist_in: rest.waist ? Math.round(cmToInches(rest.waist)) : "",
        hips_in: rest.hips ? Math.round(cmToInches(rest.hips)) : "",
      });
      setBio(b || "");
      setCategories((c || []).join(", "));
    }
    api.get("/my/applications").then((r) => setApps(r.data)).catch(() => {});
  }, [user]);

  /* Shortlist reply: message the house back about the call */
  const sendReply = async (appId) => {
    const message = (replyText[appId] || "").trim();
    if (!message) return;
    setSendingReply(appId);
    try {
      const { data } = await api.post(`/applications/${appId}/reply`, { message });
      setApps((prev) => prev.map((a) => (a.id === appId ? { ...a, replies: [...(a.replies || []), data.reply] } : a)));
      setReplyText({ ...replyText, [appId]: "" });
      toast.success("Reply sent to the house.");
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setSendingReply(null);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, bio, categories: categories.split(",").map((s) => s.trim()).filter(Boolean) };
      payload.height_cm = heightToCm(form.height_ft, form.height_in);
      BODY_MEASUREMENTS.forEach(([k]) => {
        payload[k] = inchesToCm(form[`${k}_in`]);
        delete payload[`${k}_in`];
      });
      delete payload.height_ft;
      delete payload.height_in;
      const { data } = await api.put("/profile", payload);
      setUser(data);
      toast.success("Your book is updated.");
      return true;
    } catch (e) {
      toast.error(fmtErr(e));
      return false;
    } finally {
      setSaving(false);
    }
  };

  /* Live preview: the real public portfolio rendered with the current, unsaved edits */
  const previewModel = {
    id: user.id,
    slug: user.slug,
    name: form.name || user.name,
    city: form.city || "",
    gender: form.gender || "",
    shoe: form.shoe || "",
    hair: form.hair || "",
    eyes: form.eyes || "",
    instagram: form.instagram || "",
    bio,
    categories: categories.split(",").map((c) => c.trim()).filter(Boolean),
    height_cm: heightToCm(form.height_ft, form.height_in),
    bust: form.bust_in !== "" && form.bust_in != null ? Math.round(inchesToCm(Number(form.bust_in))) : null,
    waist: form.waist_in !== "" && form.waist_in != null ? Math.round(inchesToCm(Number(form.waist_in))) : null,
    hips: form.hips_in !== "" && form.hips_in != null ? Math.round(inchesToCm(Number(form.hips_in))) : null,
    photo_url: user.profile?.photo_url,
    photos: user.profile?.photos || [],
    reels: user.profile?.reels || [],
    reel_thumbs: user.profile?.reel_thumbs || [],
    work_history: user.profile?.work_history || [],
  };

  return (
    <div className="px-6 sm:px-10 py-24" data-testid="model-dashboard">
      <Reveal>
        <p className="overline-label mb-4">Talent Profile</p>
        <div className="flex flex-wrap items-end justify-between gap-6 mb-16">
          <h1 className="font-display font-light text-4xl sm:text-6xl tracking-tighter">
            Your <span className="italic text-accent">Book</span>
          </h1>
          <button
            data-testid="preview-book-button"
            onClick={() => setShowPreview(true)}
            className="btn-outline inline-flex items-center gap-2"
          >
            <Eye size={15} /> Preview my page
          </button>
        </div>
      </Reveal>

      <BookStrength />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
        <div className="lg:col-span-7">
          <div className="panel p-8" data-testid="profile-form">
            <p className="overline-label !font-semibold !text-white/85 mb-8">Measurements & details</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
              {FIELDS.map(([key, label, type]) => (
                <div key={key}>
                  <label className="overline-label !text-[10px] !font-semibold !text-white/80 block mb-1">{label}</label>
                  <input
                    data-testid={`profile-${key}-input`}
                    type={type}
                    value={form[key] ?? ""}
                    onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                    className="field"
                  />
                </div>
              ))}
              <div>
                <label className="overline-label !text-[10px] !font-semibold !text-white/80 block mb-1">Height</label>
                <div className="flex items-center gap-3">
                  <input
                    data-testid="profile-height-ft-input"
                    type="number" min="3" max="7" placeholder="5"
                    value={form.height_ft ?? ""}
                    onChange={(e) => setForm({ ...form, height_ft: e.target.value })}
                    className="field"
                  />
                  <span className="font-grotesk text-xs text-white/50 shrink-0">ft</span>
                  <input
                    data-testid="profile-height-in-input"
                    type="number" min="0" max="11" placeholder="9"
                    value={form.height_in ?? ""}
                    onChange={(e) => setForm({ ...form, height_in: e.target.value })}
                    className="field"
                  />
                  <span className="font-grotesk text-xs text-white/50 shrink-0">in</span>
                </div>
              </div>
              {BODY_MEASUREMENTS.map(([key, label]) => (
                <div key={key}>
                  <label className="overline-label !text-[10px] !font-semibold !text-white/80 block mb-1">{label} (inches)</label>
                  <input
                    data-testid={`profile-${key}-input`}
                    type="number" step="0.5" min="0" placeholder="34"
                    value={form[`${key}_in`] ?? ""}
                    onChange={(e) => setForm({ ...form, [`${key}_in`]: e.target.value })}
                    className="field"
                  />
                </div>
              ))}
              <div className="sm:col-span-2">
                <label className="overline-label !text-[10px] !font-semibold !text-white/80 block mb-1">Categories (comma separated)</label>
                <input data-testid="profile-categories-input" value={categories} onChange={(e) => setCategories(e.target.value)} className="field" placeholder="Editorial, Runway, Campaign" />
              </div>
              <div className="sm:col-span-2">
                <label className="overline-label !text-[10px] !font-semibold !text-white/80 block mb-1">Bio — blank line between paragraphs shows beautifully on your page</label>
                <textarea data-testid="profile-bio-input" rows={3} value={bio} onChange={(e) => setBio(e.target.value)} className="field resize-none" />
              </div>
              <div className="sm:col-span-2 flex items-center justify-between gap-4 border border-white/10 px-5 py-4">
                <div>
                  <p className="overline-label !text-[10px] !font-semibold !text-white/80">New call alerts</p>
                  <p className="font-grotesk text-xs text-white/40 mt-1">Email me when an open call matches my categories</p>
                </div>
                <button
                  type="button"
                  data-testid="alerts-toggle"
                  onClick={() => setForm({ ...form, alerts: form.alerts === false })}
                  className={`font-grotesk text-[10px] uppercase tracking-[0.15em] border px-4 py-2 transition-colors ${form.alerts === false ? "border-white/15 text-white/40" : "border-accent text-accent"}`}
                >
                  {form.alerts === false ? "Off" : "On"}
                </button>
              </div>
            </div>
            <button data-testid="profile-save-button" onClick={save} disabled={saving} className="btn-primary mt-10 disabled:opacity-40">
              {saving ? "Saving…" : "Save my book"}
            </button>
          </div>

          <WorkHistoryEditor />
        </div>

        <div className="lg:col-span-5">
          <MembershipCard user={user} />
          <PortfolioManager />
          <Link
            to="/portfolio-guide"
            data-testid="portfolio-guide-link"
            className="mt-4 inline-flex items-center gap-2 font-grotesk text-[10px] uppercase tracking-[0.18em] text-accent hover:text-white transition-colors"
          >
            <Camera size={13} /> Read the Portfolio Standard — the five frames houses expect, and how your comp card uses them
          </Link>
          <ReelManager />
          <DocumentsManager hint="Releases, IDs, portfolio PDFs — the office always sees these; a brand sees them once you apply to their call." />
          <ChangePassword />
          <div className="panel p-8 mt-10" data-testid="my-applications">
            <p className="overline-label !font-semibold !text-white/85 mb-8">My applications ({apps.length})</p>
            {user?.plan?.on_hold && apps.length > 0 && (
              <p className="font-grotesk text-[11px] uppercase tracking-[0.12em] text-accent mb-6" data-testid="applications-held-note">
                Outcomes are hidden while your membership is on hold — update your payment method to see them
              </p>
            )}
            {apps.length === 0 ? (
              <p className="font-display italic text-2xl text-white/40">No applications yet — the calls are waiting.</p>
            ) : (
              <div className="flex flex-col">
                {apps.map((a) => (
                  <div key={a.id} className="py-5 border-b border-white/10 last:border-0" data-testid={`application-${a.id}`}>
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <p className="font-display text-xl">{a.casting_title}</p>
                        <p className="overline-label !text-[10px] mt-1">{a.brand}</p>
                        {a.response_note && (
                          <p className="text-sm text-accent/90 italic mt-2" data-testid={`note-${a.id}`}>&ldquo;{a.response_note}&rdquo;</p>
                        )}
                      </div>
                      <span className={`font-grotesk text-[10px] uppercase tracking-[0.15em] border px-3 py-1.5 ${STATUS_STYLE[a.status] || STATUS_STYLE.pending}`}>
                        {a.status === "held" ? "Hidden" : a.status}
                      </span>
                    </div>
                    {(a.status === "shortlisted" || a.status === "booked") && (
                      <div className="mt-4" data-testid={`reply-area-${a.id}`}>
                        {(a.replies || []).map((r, i) => (
                          <p key={i} className="text-sm text-white/70 italic mb-2" data-testid={`reply-${a.id}-${i}`}>
                            <span className="not-italic font-grotesk text-[9px] uppercase tracking-[0.12em] text-accent mr-2">You</span>
                            &ldquo;{r.message}&rdquo;
                          </p>
                        ))}
                        <div className="flex gap-2 mt-3">
                          <input
                            data-testid={`reply-input-${a.id}`}
                            value={replyText[a.id] || ""}
                            onChange={(e) => setReplyText({ ...replyText, [a.id]: e.target.value })}
                            placeholder="Reply to the house — availability, thanks, questions…"
                            className="field !py-2 !text-sm flex-1"
                            maxLength={1000}
                          />
                          <button
                            data-testid={`reply-send-${a.id}`}
                            onClick={() => sendReply(a.id)}
                            disabled={sendingReply === a.id || !(replyText[a.id] || "").trim()}
                            className="btn-outline !px-4 !py-2 disabled:opacity-40"
                          >
                            {sendingReply === a.id ? "…" : "Send"}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {showPreview && (
        <div className="fixed inset-0 z-[70] bg-background overflow-y-auto" data-testid="book-preview">
          <div className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b border-accent/30 px-6 sm:px-10 py-4 flex items-center justify-between gap-4 flex-wrap">
            <p className="font-grotesk text-[10px] uppercase tracking-[0.18em] text-accent" data-testid="book-preview-note">
              Live preview — unsaved edits shown
            </p>
            <div className="flex items-center gap-3">
              <button
                data-testid="book-preview-save"
                onClick={async () => { if (await save()) setShowPreview(false); }}
                disabled={saving}
                className="btn-primary !py-2 !px-4 disabled:opacity-40"
              >
                {saving ? "Publishing…" : "Save changes & publish"}
              </button>
              <button data-testid="book-preview-close" onClick={() => setShowPreview(false)} className="btn-outline !py-2 !px-4 inline-flex items-center gap-2">
                <X size={14} /> Close preview
              </button>
            </div>
          </div>
          <PortfolioView model={previewModel} showBack={false} />
        </div>
      )}
    </div>
  );
}


function MembershipCard({ user }) {
  const [busy, setBusy] = useState(false);
  const plan = user?.plan;
  const trial = plan?.trial && new Date(plan.trial_ends) > new Date();
  const active = plan?.key === "plan_talent" || trial;
  const onHold = !!plan?.on_hold;
  if (!active) return null;

  const openPortal = async () => {
    setBusy(true);
    try {
      const r = await api.post("/payments/portal", { origin_url: window.location.origin });
      window.top.location.href = r.data.portal_url;
    } catch (e) {
      toast.error(fmtErr(e));
      setBusy(false);
    }
  };

  return (
    <div className={`panel p-6 mb-10 flex items-center justify-between gap-4 ${onHold ? "!border-accent" : ""}`} data-testid="membership-status">
      <div>
        <p className="overline-label !text-[10px] mb-1">Talent Pass</p>
        <p className="font-grotesk text-xs text-white/70" data-testid="membership-hold-note">
          {onHold
            ? "On hold — we couldn't charge your card. Update your payment method to keep applying."
            : trial ? `Trial — active until ${new Date(plan.trial_ends).toDateString()}` : "Active — renews monthly"}
        </p>
        {onHold && plan.hold_reason ? (
          <p className="text-xs text-white/60 mt-2" data-testid="hold-reason">A note from the office: <em>"{plan.hold_reason}"</em></p>
        ) : null}
      </div>
      {plan?.stripe_subscription_id && (
        <button data-testid="manage-membership-button" onClick={openPortal} disabled={busy} className={`${onHold ? "btn-primary" : "btn-outline"} !px-4 !py-2.5`}>
          {busy ? "Opening…" : onHold ? "Update payment method" : "Manage membership"}
        </button>
      )}
    </div>
  );
}
